'use client';

import {
  AdvancedSVG,
  CheckSVG,
  EvolutionSVG,
  MlSpaceSVG,
  VmwareSVG,
} from '@sbercloud/uikit-product-icons';
import { Card } from '@snack-uikit/card';
import { Tabs } from '@snack-uikit/tabs';
import { QuestionTooltip } from '@snack-uikit/tooltip';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { Wrapper } from 'src/components/Wrapper';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useHeaderPosition } from 'src/hooks/useHeaderPosition';
import { TypographyFrame, useWindowWidth } from 'src/hooks/useWindowWidth';
import { Layout } from 'src/layout';
import { HeroZero, HeroZeroProps } from 'src/snack/components/HeroZero';
import { Form } from 'src/uikit/Form';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import s from './Comparison.module.scss';

type Cell = {
  isCriteria?: boolean;
  title: string;
  tooltip?: string;
};

type TableData = {
  header: Cell[];
  body: CellType[][];
};

type CheckedType = { title: 'checked'; isCriteria?: boolean; tooltip?: string };
type CellType = CheckedType | Cell;

const CHECKED: CheckedType = { title: 'checked' };

const MOCK_PLATFORMS: TableData = {
  header: [
    {
      title: 'Критерий',
      isCriteria: true,
    },
    { title: 'Evolution' },
    { title: 'Advanced' },
    { title: 'VMware' },
    { title: 'ML Space' },
  ],
  body: [
    [
      { title: 'Наличие управляемых сервисов' },
      CHECKED,
      CHECKED,
      { title: '—' },
      { title: 'Да' },
    ],
    [
      { title: 'Год запуска' },
      { title: '2024' },
      { title: '2020' },
      { title: '2019' },
      { title: '2020' },
    ],
    [
      {
        title:
          'Контроль исходного кода и технологическая независимость платформы',
      },
      { title: 'Полный' },
      { title: '—' },
      { title: '—' },
      { title: 'Полный' },
    ],
    [
      { title: 'Собственная инфраструктура' },
      CHECKED,
      CHECKED,
      CHECKED,
      CHECKED,
    ],
    [
      { title: 'Количество PaaS-сервисов' },
      { title: '20+' },
      { title: '60+' },
      { title: '—' },
      { title: 'Вся платформа — PaaS' },
    ],
    [
      {
        title: 'Количество спецификаций виртуальных машин',
        tooltip:
          '<a href="https://cloud.ru/docs/vdc/ug/topics/overview__limitations__vm.html" target="_blank" class="link">Ограничения для параметров VM</a>',
      },
      { title: '50' },
      { title: '128' },
      { title: 'Гибкая конфигурация' },
      { title: '—' },
    ],
    [
      { title: 'Модели графических ускорителей' },
      {
        title: `
        <ul>
          <li>NVIDIA V100 32 ГБ</li>
          <li>NVIDIA H100 80 ГБ</li>
        </ul>
        `,
      },
      {
        title: `
        <ul>
          <li>NVIDIA V100 16 ГБ</li>
          <li>NVIDIA V100 32 ГБ</li>
          <li>NVIDIA A100 80 ГБ</li>
        </ul>
        `,
      },
      {
        title: `<ul>
        <li>NVIDIA V100 32 ГБ</li>
        <li>NVIDIA A100 40 ГБ</li>
        <li>NVIDIA A100 80 ГБ</li>
        <li>NVIDIA T4 16 ГБ</li>
        <li>NVIDIA A40 48 ГБ</li>
        <li>NVIDIA A16 64 ГБ</li>
        <li>NVIDIA H100 80 ГБ</li>
      </ul>`,
      },
      {
        title: `
        <ul>
          <li>NVIDIA A100 80 ГБ</li>
          <li>NVIDIA H100 80 ГБ</li>
        </ul>`,
      },
    ],
    [
      { title: 'Уровень SLA' },
      { title: '99,9 %' },
      { title: '99,95 %' },
      { title: '99,982 %' },
      { title: '99,95 %' },
    ],
    [
      { title: 'Уровень отказоустойчивости' },
      { title: '2 зоны' },
      { title: '4 зоны' },
      { title: '3 зоны' },
      { title: '3 зоны' },
    ],
    [
      { title: 'Наличие средств миграции на платформу' },
      { title: 'Внешние' },
      { title: 'Встроенные' },
      { title: 'Встроенные, внешние' },
      { title: 'Не применимо' },
    ],
    [
      { title: 'Документация' },
      { title: 'На русском' },
      { title: 'На русском и английском' },
      { title: 'На русском' },
      { title: 'На русском' },
    ],
    [
      { title: 'Наличие лабораторных работ' },
      CHECKED,
      CHECKED,
      CHECKED,
      { title: '—' },
    ],
    [
      { title: 'Наличие обучений и сертификаций' },
      CHECKED,
      CHECKED,
      CHECKED,
      CHECKED,
    ],
    [
      { title: 'Поддержка миграции профессиональными сервисами' },
      { title: 'На уровне IaaS - да<br/>На уровне PaaS - частично' },
      { title: 'На уровне IaaS - да<br/>На уровне PaaS - частично' },
      CHECKED,
      { title: '—' },
    ],
    [
      { title: 'Наличие аттестата PCI DSS' },
      { title: 'В планах' },
      CHECKED,
      CHECKED,
      { title: '—' },
    ],
    [{ title: 'Наличие аттестата 152-ФЗ' }, CHECKED, CHECKED, CHECKED, CHECKED],
    [
      {
        title: 'ГОСТ 57580',
        tooltip: 'Стандарт безопасности банковских и финансовых операций',
      },
      { title: 'В планах' },
      { title: '—' },
      CHECKED,
      { title: '—' },
    ],
    [
      {
        title: 'ISO 27001',
        tooltip:
          'Стандарт по созданию, развитию и поддержанию Системы менеджмента информационной безопасности (СМИБ)',
      },
      { title: 'В планах' },
      { title: '—' },
      CHECKED,
      CHECKED,
    ],
    [
      {
        title: 'ISO 27017',
        tooltip:
          'Стандарт по обеспечению информационной безопасности для облачных провайдеров и пользователей облачных услуг',
      },
      { title: 'В планах' },
      { title: '—' },
      CHECKED,
      CHECKED,
    ],
    [
      {
        title: 'ISO 27018',
        tooltip:
          'Стандарт по обеспечению защиты персональных данных при их обработке провайдерами облачных услуг',
      },
      { title: 'В планах' },
      { title: '—' },
      CHECKED,
      CHECKED,
    ],
    [
      {
        title: 'ISO 27701',
        tooltip:
          'Стандарт по управлению персональными данными (PIMS), является расширением стандарта ISO 27001',
      },
      { title: 'В планах' },
      { title: '—' },
      CHECKED,
      CHECKED,
    ],
    [
      { title: 'Возможность создания гибридного или частного облака' },
      CHECKED,
      { title: '—' },
      { title: 'Частное облако' },
      { title: 'В планах' },
    ],
    [
      { title: 'Возможность использования Bare Metal' },
      CHECKED,
      { title: '—' },
      CHECKED,
      { title: '—' },
    ],
    [
      { title: 'Виртуальная машина без переподписки' },
      CHECKED,
      CHECKED,
      { title: 'В рамках частного облака' },
      { title: '—' },
    ],
    [
      { title: 'Вычислительные мощности с повышенной тактовой частотой' },
      CHECKED,
      CHECKED,
      CHECKED,
      { title: '—' },
    ],
    [
      {
        title: 'Pay-as-you-go',
        tooltip: 'Модель оплаты по мере потребления облачных сервисов',
      },
      CHECKED,
      CHECKED,
      CHECKED,
      CHECKED,
    ],
    [
      {
        title: 'Pay-as-you-allocate',
        tooltip:
          'Модель полной оплаты облачных сервисов независимо от фактического потребления',
      },
      { title: '—' },
      { title: '—' },
      CHECKED,
      CHECKED,
    ],
    [
      { title: 'Местоположение ЦОД' },
      { title: 'Московский регион' },
      { title: 'Московский регион' },
      { title: 'Московский регион' },
      { title: 'Московский регион' },
    ],
    [
      { title: 'Количество географически разнесенных точек подключения' },
      { title: '2' },
      { title: '2' },
      { title: '2' },
      { title: '—' },
    ],
    [
      {
        title: 'Наличие бесплатных сервисов',
        tooltip:
          'Важно: не все продукты Cloud.ru бесплатны. У некоторых продуктов есть квоты бесплатного использования. Если вы превысите квоту, то с вас будет взиматься плата по стандартным тарифам.',
      },
      CHECKED,
      CHECKED,
      CHECKED,
      CHECKED,
    ],
    [
      { title: 'Управление квотами и лимитами на ресурсы и сервисы' },
      CHECKED,
      CHECKED,
      CHECKED,
      CHECKED,
    ],
    [
      { title: 'Прогнозирование использования ресурсов и сервисов' },
      { title: 'Сервис личного кабинета' },
      { title: 'Сервис личного кабинета' },
      { title: 'Сервис личного кабинета' },
      { title: 'Сервис личного кабинета' },
    ],
    [
      { title: 'Отчеты по утилизации ресурсов и сервисов' },
      { title: 'Сервис мониторинга в личном кабинете' },
      { title: 'Собственный сервис мониторинга' },
      {
        title: `
        <ul>
          <li>Сервис мониторинга в личном кабинете</li>
          <li>Собственные средства мониторинга</li>
        </ul>
        `,
      },
      { title: 'В планах' },
    ],
    [
      { title: 'Мониторинг доступности используемых сервисов' },
      { title: 'В планах' },
      CHECKED,
      { title: 'В планах' },
      { title: 'В планах' },
    ],
    [
      { title: 'Технологии автоматизации работы с платформой' },
      {
        title: `
        <ul>
          <li>Cloud API</li>
          <li>Terraform Provider</li>
          <li>Ansible</li>
        </ul>
        `,
      },
      {
        title: `
        <ul>
          <li>Cloud API</li>
          <li>Terraform Provider</li>
          <li>Ansible</li>
          <li>CLI</li>
        </ul>`,
      },
      {
        title: `
        <ul>
          <li>Cloud API</li>
          <li>Terraform Provider</li>
          <li>Ansible</li>
          <li>PowerCLI</li>
        </ul>
        `,
      },
      {
        title: `
        <ul>
          <li>API</li>
        </ul>
        `,
      },
    ],
  ],
};

const MOCK_SERVICES_TABLES: TableData[] = [
  {
    header: [
      { title: 'Виртуальные машины и вычисления', isCriteria: true },
      { title: 'Evolution' },
      { title: 'Advanced' },
      { title: 'VMware' },
    ],
    body: [
      [
        { title: 'Типы ВМ' },
        { title: 'Предопределенные конфигурации' },
        { title: 'Предопределенные конфигурации' },
        { title: 'Произвольные' },
      ],
      [
        { title: 'Наличие сервиса автомасштабирования ВМ' },
        { title: '—' },
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Гипервизоры' },
        { title: 'KVM' },
        { title: 'KVM' },
        { title: 'VMware' },
      ],
      [
        { title: 'Максимальный размер диска' },
        { title: '16 ТБ' },
        { title: '1,5 ТБ для системного диска<br />32ТБ для остальных' },
        { title: '4 ТБ' },
      ],
      [
        { title: 'Максимальное количество дисков на ВМ' },
        { title: '8' },
        { title: '24' },
        { title: '120' },
      ],
      [
        { title: 'Возможность изменения характеристик ВМ' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Максимальные IOPS на дисках ВМ' },
        { title: '20 000' },
        { title: '128 000' },
        { title: 'Bronze - 100 на 1 ТБ<br />Gold - 5 000 на 1 ТБ' },
      ],
      [
        { title: 'Максимальная пропускная способность дисков' },
        { title: '782 МБ/c' },
        { title: '1 000 МБ/с' },
        { title: '1 280 МБ/с' },
      ],
      [
        { title: 'Максимальная сетевая пропускная способность ВМ' },
        { title: '40 Гбит/с' },
        { title: '40 Гбит/с' },
        { title: '10 Гбит/с' },
      ],
      [
        { title: 'Максимальное количество ядер на ВМ' },
        { title: '40' },
        { title: '96' },
        { title: '28 (128 и 160 ядер доступно для ЧО)' },
      ],
      [
        { title: 'Максимальное количество оперативной памяти на ВМ' },
        { title: '320 ГБ' },
        { title: '768 ГБ' },
        { title: '768 ГБ (до 3072 ГБ доступно для ЧО)' },
      ],
      [{ title: 'Шифрование дисков' }, { title: 'В планах' }, CHECKED, CHECKED],
      [
        { title: 'Максимальное количество сетевых интерфейсов' },
        { title: '8' },
        { title: '8' },
        { title: '10' },
      ],
      [
        { title: 'Использование собственных образов ВМ' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [{ title: 'Возможность приостановки ВМ' }, CHECKED, CHECKED, CHECKED],
      [
        { title: 'Наличие встроенного сервиса резервного копирования ВМ' },
        { title: 'В планах' },
        CHECKED,
        CHECKED,
      ],
    ],
  },
  {
    header: [
      { title: 'Контейнеры', isCriteria: true },
      { title: 'Evolution' },
      { title: 'Advanced' },
      { title: 'VMware' },
    ],
    body: [
      [
        { title: 'Наличие управляемого сервиса Kubernetes' },
        CHECKED,
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Наличие Persistant Volume для управляемого kubernetes' },
        CHECKED,
        CHECKED,
        { title: '—' },
      ],
      [
        {
          title: 'Наличие ingress-контроллера на базе балансировщика нагрузки',
        },
        CHECKED,
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Поддержка GPU в управляемом Kubernetes' },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
      [{ title: 'Поддержка Calico' }, CHECKED, { title: '—' }, { title: '—' }],
      [{ title: 'Поддержка Cilium' }, CHECKED, { title: '—' }, { title: '—' }],
      [
        { title: 'Запуск контейнеров без Kubernetes' },
        CHECKED,
        { title: '—' },
        { title: '—' },
      ],
      [
        { title: 'Наличие встроенного реестра контейнеров' },
        CHECKED,
        CHECKED,
        { title: '—' },
      ],
    ],
  },
  {
    header: [
      { title: 'Хранилища', isCriteria: true },
      { title: 'Evolution' },
      { title: 'Advanced' },
      { title: 'VMware' },
    ],
    body: [
      [
        { title: 'Наличие управляемого файлового хранилища' },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Наличие объектного хранилища' },
        CHECKED,
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Мультизональность для объектного хранилища' },
        CHECKED,
        CHECKED,
        { title: '—' },
      ],
    ],
  },
  {
    header: [
      { title: 'Виртуальная сеть', isCriteria: true },
      { title: 'Evolution' },
      { title: 'Advanced' },
      { title: 'VMware' },
    ],
    body: [
      [
        { title: 'Наличие L4-балансировщика' },
        CHECKED,
        CHECKED,
        { title: 'Да, часть PD' },
      ],
      [
        { title: 'Наличие L7-балансировщика' },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Наличие подключения к платформе по оптике' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Плоская сеть между различными зонами доступности' },
        { title: '—' },
        CHECKED,
        { title: '—' },
      ],
      [{ title: 'Приватные IP-адреса' }, CHECKED, CHECKED, CHECKED],
      [{ title: 'Публичные IP-адреса' }, CHECKED, CHECKED, CHECKED],
      [{ title: 'Наличие NAT gateway' }, CHECKED, CHECKED, CHECKED],
      [
        { title: 'Наличие встроенного L3 VPN (IPSec)' },
        { title: 'В планах' },
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Фильтрация трафика на уровне ресурса' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Фильтрация трафика на уровне виртуальной сети' },
        { title: 'В планах' },
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Система доменных имен' },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Наличие сервиса WAF' },
        { title: '—' },
        CHECKED,
        { title: '—' },
      ],
      [
        { title: 'Наличие API Gateway' },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
    ],
  },
  {
    header: [
      { title: 'Контроль и управление', isCriteria: true },
      { title: 'Evolution' },
      { title: 'Advanced' },
      { title: 'VMware' },
    ],
    body: [
      [
        { title: 'Регистрация событий и действий пользователей' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        {
          title: 'Поддержка собственных ролей и разрешений RBAC',
        },
        { title: 'В планах' },
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Сквозной поиск событий в платформе' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        { title: 'Защита от переполнения журналов событий' },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [{ title: 'Оповещения по событиям' }, CHECKED, CHECKED, CHECKED],
      [
        {
          title:
            'Настраиваемые параметры мониторинга сервисов платформы (дашборды)',
        },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        {
          title: 'Настраиваемые параметры мониторинга сервисов платформы',
        },
        { title: 'В планах' },
        CHECKED,
        CHECKED,
      ],
      [
        {
          title:
            'Cинхронизации с внешними системами: SIEM, мониторинг, логирование',
        },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        {
          title: 'Поддержка двухфакторной аутентификации пользователей',
        },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        {
          title:
            'Поддержка внешних каталогов и систем аутентификации пользователей',
        },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        {
          title: 'Наличие встроенного сканера безопасности',
        },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
      [
        {
          title: 'Наличие веб-консоли ВМ',
        },
        CHECKED,
        CHECKED,
        CHECKED,
      ],
      [
        {
          title: 'Удаленный рабочий стол',
        },
        { title: 'В планах' },
        { title: '—' },
        CHECKED,
      ],
      [
        {
          title: 'PAM-сервисы',
        },
        { title: 'В планах' },
        CHECKED,
        { title: '—' },
      ],
    ],
  },
];

const ID_PLATFORMS = 'platform_cards';
const ID_FORM = 'form';

const Table = ({ tables }: { tables: TableData[] }) => {
  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();
  const { showHeader, headerHeight } = useHeaderPosition();

  return (
    <div className={s.container}>
      {tables.map(({ header, body }, index) => (
        <table
          key={index}
          className={s.table}
          style={tables.length > 1 ? { tableLayout: 'fixed' } : {}}
        >
          <thead>
            <tr>
              {header.map((cell, index) => {
                const label: TypographyFrame = {
                  purpose: 'label',
                  size: 'l',
                };
                const body: TypographyFrame = {
                  purpose: 'body',
                  size: 'm',
                };

                const properties = cell.isCriteria ? body : label;

                const tdStyles = index === 0 ? [s.stickyLeft] : [s.header];

                return (
                  <th
                    key={`head-cell-${index}`}
                    className={cn(s.cell, s.sticky, tdStyles)}
                    style={
                      windowWidth && windowWidth > MD
                        ? { top: `${showHeader ? headerHeight : 0}px` }
                        : {}
                    }
                  >
                    <Typography
                      {...properties}
                      family="sans"
                      tag="p"
                      className={cn(s.typography, {
                        [s.grey]: cell.isCriteria,
                      })}
                    >
                      {cell.title}
                      {cell.tooltip && (
                        <QuestionTooltip
                          className={s.tooltip}
                          tip={cell.tooltip}
                        />
                      )}
                    </Typography>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {body.map((row, indexRow) => {
              const isNextRowCriteria =
                indexRow !== body.length - 1 &&
                body[indexRow + 1][0]?.isCriteria;

              return (
                <tr
                  key={`body-row-${indexRow}`}
                  className={cn({
                    [s.sticky]: row[0].isCriteria,
                    [s.border]:
                      !isNextRowCriteria && !(indexRow === body.length - 1),
                  })}
                >
                  {row.map((cell, indexCell) => {
                    const label: TypographyFrame = {
                      purpose: 'label',
                      size: 'l',
                    };
                    const body: TypographyFrame = {
                      purpose: 'body',
                      size: 'm',
                    };

                    const properties = cell.isCriteria
                      ? body
                      : indexCell === 0
                      ? label
                      : body;

                    return (
                      <td
                        key={`body-cell-${(indexCell + 1) * (indexRow + 1)}`}
                        className={cn(s.cell, {
                          [s.stickyLeft]: indexCell === 0,
                        })}
                      >
                        <Typography
                          {...properties}
                          family={'sans'}
                          className={cn(s.typography, {
                            [s.grey]: cell.isCriteria,
                          })}
                          tag="div"
                        >
                          {cell === CHECKED ? (
                            <CheckSVG />
                          ) : (
                            <>
                              <span
                                style={{ display: 'inline' }}
                                dangerouslySetInnerHTML={{ __html: cell.title }}
                              />
                              {cell.tooltip && (
                                <QuestionTooltip
                                  className={s.tooltip}
                                  tip={
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: cell.tooltip,
                                      }}
                                    />
                                  }
                                />
                              )}
                            </>
                          )}
                        </Typography>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      ))}
    </div>
  );
};

const CardContent = ({
  icon,
  title,
  description,
}: {
  icon: JSX.Element;
  title: string;
  description: string;
}) => {
  const { getAdaptiveSizeTypography } = useWindowWidth();

  const cardsTitleTypographyProps = getAdaptiveSizeTypography({
    desktop: {
      purpose: 'title',
      size: 'l',
    },
    tablet: {
      purpose: 'title',
      size: 'm',
    },
  });

  const cardsDescriptionTypographyProps = getAdaptiveSizeTypography({
    desktop: {
      purpose: 'body',
      size: 'l',
    },
    tablet: {
      purpose: 'body',
      size: 'm',
    },
  });

  return (
    <div className={s.cardWrapper}>
      {icon}
      <div className={s.text}>
        <Typography
          className={s.title}
          family="sans"
          {...cardsTitleTypographyProps}
        >
          {title}
        </Typography>
        <Typography
          className={s.description}
          family="sans"
          {...cardsDescriptionTypographyProps}
        >
          {description}
        </Typography>
      </div>
    </div>
  );
};

const Platforms = () => {
  const { getAdaptiveSizeTypography } = useWindowWidth();
  const { clickAnalytics } = useAnalytics();
  const router = useRouter();

  const headerTypographyProps = getAdaptiveSizeTypography({
    desktop: {
      purpose: 'headline',
      size: 'l',
    },
    tablet: {
      purpose: 'title',
      size: 'l',
    },
  });

  return (
    <Wrapper className={s.wrapper}>
      <Typography family={'sans'} {...headerTypographyProps}>
        Платформы Cloud.ru
      </Typography>

      <div className={s.cards}>
        <Card
          size="m"
          className={s.card}
          href="/evolution"
          onClick={(e) => {
            e.preventDefault();
            clickAnalytics({
              action: '/evolution',
              clickZone: 'body',
              clickElement: 'cardclick',
              clickContent: 'Evolution',
              uniqueId: `platforms-comparison-platform-cards-Evolution`,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: '/evolution',
              transitionTypeMetrics: 'external',
            });
            router.push('/evolution');
          }}
        >
          <CardContent
            icon={<EvolutionSVG />}
            title="Evolution"
            description="Активно развивающаяся платформа для любых сценариев"
          />
        </Card>

        <Card
          size="m"
          className={s.card}
          href="/advanced"
          onClick={(e) => {
            e.preventDefault();
            clickAnalytics({
              action: '/advanced',
              clickZone: 'body',
              clickElement: 'cardclick',
              clickContent: 'Advanced',
              uniqueId: `platforms-comparison-platform-cards-Advanced`,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: '/advanced',
              transitionTypeMetrics: 'internal',
            });
            router.push('/advanced');
          }}
        >
          <CardContent
            icon={<AdvancedSVG />}
            title="Advanced"
            description="60+ IaaS- и PaaS-сервисов для развития инфраструктуры"
          />
        </Card>

        <Card
          size="m"
          className={s.card}
          href="/mlspace"
          onClick={(e) => {
            e.preventDefault();
            clickAnalytics({
              action: '/mlspace',
              clickZone: 'body',
              clickElement: 'cardclick',
              clickContent: 'ML Space',
              uniqueId: `platforms-comparison-platform-cards-ML Space`,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: '/mlspace',
              transitionTypeMetrics: 'internal',
            });
            router.push('/mlspace');
          }}
        >
          <CardContent
            icon={<MlSpaceSVG />}
            title="ML Space"
            description="Суперкомпьютеры и ML для создания приложений"
          />
        </Card>

        <Card
          size="m"
          className={s.card}
          href="/vmware"
          onClick={(e) => {
            e.preventDefault();
            clickAnalytics({
              action: '/vmware',
              clickZone: 'body',
              clickElement: 'cardclick',
              clickContent: 'Облако VMware',
              uniqueId: `platforms-comparison-platform-cards-Облако VMware`,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: '/vmware',
              transitionTypeMetrics: 'internal',
            });
            router.push('/vmware');
          }}
        >
          <CardContent
            icon={<VmwareSVG />}
            title="Облако VMware"
            description="Облачная инфраструктура на базе технологий VMware"
          />
        </Card>
      </div>
    </Wrapper>
  );
};

enum TabsEnum {
  platforms = 'platforms',
  services = 'services',
}

export function PlatformsComparisonPage() {
  const { clickAnalytics } = useAnalytics();
  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();

  const [value, setValue] = useState(TabsEnum.platforms);

  const TablePlatforms = () => (
    <>
      {windowWidth && windowWidth > MD ? (
        <Wrapper>
          <Table tables={[MOCK_PLATFORMS]} />
        </Wrapper>
      ) : (
        <Table tables={[MOCK_PLATFORMS]} />
      )}
    </>
  );

  const TableServices = () => (
    <>
      {windowWidth && windowWidth > MD ? (
        <Wrapper>
          <Table tables={MOCK_SERVICES_TABLES} />
        </Wrapper>
      ) : (
        <Table tables={MOCK_SERVICES_TABLES} />
      )}
    </>
  );

  const hero: HeroZeroProps = {
    title: 'Сравнение платформ Cloud.ru',
    description:
      'Выберите подходящую облачную платформу под ваши задачи и требования.',
    breadcrumbs: [
      {
        id: 'main',
        label: 'Главная',
        href: '/',
        onClick: () => {
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'breadcrumb',
            clickContent: 'Главная',
            uniqueId: 'comparison-breadcrumbs-go-index-Главная',
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
          });
        },
      },
      {
        id: 'products',
        label: 'Продукты',
        href: '/products',
        onClick: () => {
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'breadcrumb',
            clickContent: 'Главная',
            uniqueId: 'comparison-breadcrumbs-Продукты',
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
          });
        },
      },
      {
        id: 'comparison',
        label: 'Сравнение платформ',
      },
    ],

    buttons: [
      {
        size: 'l',
        appearance: 'primary',
        label: 'Получить консультацию',
        onClick: () => {
          scrollToElementWithId(ID_FORM);
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'button',
            clickContent: 'Получить консультацию',
            uniqueId: 'platforms-comparison-hero-zero-cta-scroll-to-form',
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
          });
        },
      },
      {
        size: 'l',
        appearance: 'neutral',
        label: 'Платформы',
        onClick: () => {
          scrollToElementWithId(ID_PLATFORMS);
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'button',
            clickContent: 'Платформы',
            uniqueId:
              'platforms-comparison-hero-zero-cta-scroll-to-platform-cards',
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
          });
        },
      },
    ],
  };

  return (
    <Layout>
      <div className={s.root}>
        <HeroZero {...hero} />
        <section className={s.content}>
          <Tabs
            value={value}
            onChange={setValue}
            defaultValue={TabsEnum.platforms}
          >
            <Wrapper className={s.wrapper}>
              <Tabs.TabBar className={s.tabbar}>
                <Tabs.Tab
                  label="Общие характеристики"
                  value={TabsEnum.platforms}
                  onClick={() => {
                    clickAnalytics({
                      action: 'click',
                      clickZone: 'body',
                      clickElement: 'button',
                      clickContent: 'Общие характеристики',
                      uniqueId: `platforms-comparison-table-nav-bar-Общие характеристики`,
                      transitionType: 'inside-link',
                      clickZoneCategory: 'site-body',
                    });
                  }}
                />
                <Tabs.Tab
                  label="Сервисы"
                  value={TabsEnum.services}
                  onClick={() => {
                    clickAnalytics({
                      action: 'click',
                      clickZone: 'body',
                      clickElement: 'button',
                      clickContent: 'Сервисы',
                      uniqueId: `platforms-comparison-table-nav-bar-Сервисы`,
                      transitionType: 'inside-link',
                      clickZoneCategory: 'site-body',
                    });
                  }}
                />
              </Tabs.TabBar>
            </Wrapper>

            <Tabs.TabContent value={TabsEnum.platforms}>
              <TablePlatforms />
            </Tabs.TabContent>

            <Tabs.TabContent value={TabsEnum.services}>
              <TableServices />
            </Tabs.TabContent>
          </Tabs>
        </section>
        <section className={s.platforms} id={ID_PLATFORMS}>
          <Platforms />
        </section>
        <Form
          id={ID_FORM}
          title="Заявка на консультацию"
          inputPath="v2/order"
          formVariant="inner"
          analyticsFieldFill={{ formType: 'body' }}
          withLoader
          allFormsSentFormType="consultation"
          classNames={{
            inner: s.form,
          }}
        />
      </div>
    </Layout>
  );
}
